import { useState, lazy, Suspense } from "react";
import { useTheme } from "@/hooks/use-theme";
import { useAuth } from "@/hooks/use-auth";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TourMobileContext } from "@/hooks/use-tour-mobile";
import { TourRunningProvider } from "@/hooks/use-tour-running";
import { useTour } from "@/hooks/use-tour";

const GuidedTour = lazy(() => import("@/components/tour/GuidedTour"));
const ScreenTour = lazy(() => import("@/components/tour/ScreenTour"));
const TourFab = lazy(() => import("@/components/tour/TourFab"));
const HelpLauncher = lazy(() => import("@/components/help/HelpLauncher"));
import {
  Building2,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { SidebarNav } from "@/components/layout/SidebarNav";
import { ContextSwitcher } from "@/components/layout/ContextSwitcher";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { clientNav, partnerNav } from "@/config/navigation";
import { useActivePanel } from "@/hooks/use-active-panel";
import { cn } from "@/lib/utils";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { useWhiteLabel } from "@/hooks/use-white-label";
import { TOURS_ENABLED } from "@/lib/tours-enabled";
import { useSyncCompleteListener } from "@/hooks/useOfflineSync";
import { HelpAssistantProvider } from "@/hooks/use-help-assistant";
import { AvisoMfaSuperadmin } from "@/components/AvisoMfaSuperadmin";
import { TitularPdfSync } from "@/components/TitularPdfSync";
import { BRAND } from "@/lib/brand";

export default function AppLayout({ children }: { children: React.ReactNode }) {
  useRealtimeSync();
  // Revalida a tela quando o service worker reenvia escritas que ficaram na fila
  useSyncCompleteListener();

  const location = useLocation();
  const { user, signOut } = useAuth();
  const { logoUrl, nomeEmpresa } = useWhiteLabel();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { isDark, setTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const forceTour = searchParams.get("tour") === "1";
  // ?tour=tela refaz o tour da tela atual, sem disparar o onboarding global
  const forceScreenTour = searchParams.get("tour") === "tela";
  const { shouldShowTour } = useTour();

  const toggleTheme = () => setTheme(isDark ? "light" : "dark");
  const sidebarWidth = collapsed ? "w-16" : "w-56";
  // Um layout, dois menus. Duplicar o AppLayout para o anunciante criaria dois
  // arquivos quase iguais que divergem em silêncio — a mesma razão pela qual
  // este projeto tem um componente de ajuda só e um gatilho de ajuda só.
  const { activePanel } = useActivePanel();
  const nav = activePanel === "partner" ? partnerNav : clientNav;
  const mobileItems = nav.mobileItems ?? [];

  return (
    <TourMobileContext.Provider value={{ openSidebar: () => setSidebarOpen(true) }}>
    <div className="min-h-screen flex bg-background">
      {/* Command Palette */}
      <CommandPalette
        config={nav}
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
      />

      {/* Desktop Sidebar */}
      <aside data-tour="sidebar-desktop" className={`hidden lg:flex flex-col ${sidebarWidth} bg-sidebar border-r border-border transition-all duration-200`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-sidebar-primary flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt={nomeEmpresa ?? "Logo"} className="h-full w-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              ) : (
                <Building2 className="h-4 w-4 text-sidebar-primary-foreground" />
              )}
            </div>
            {!collapsed && <span className="text-sm font-bold text-sidebar-accent-foreground">{nomeEmpresa ?? BRAND}</span>}
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            className="text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Context Switcher */}
        <div className="border-b border-border">
          <ContextSwitcher collapsed={collapsed} />
        </div>

        {/* Nav com grupos colapsáveis */}
        <SidebarNav config={nav} collapsed={collapsed} />

        {/* Bottom */}
        <div className="px-2 pb-3 space-y-1 border-t border-border pt-3">
          {!collapsed && (
            <button
              onClick={() => setPaletteOpen(true)}
              className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-[12px] text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
              aria-label="Abrir paleta de comandos"
            >
              <span>Busca rápida</span>
              <kbd className="text-[10px] bg-sidebar-accent/50 px-1.5 py-0.5 rounded">⌘K</kbd>
            </button>
          )}
          <button
            onClick={toggleTheme}
            aria-label="Alternar tema"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent/50 transition-colors w-full"
          >
            {isDark
              ? <Sun className="h-[18px] w-[18px] shrink-0" />
              : <Moon className="h-[18px] w-[18px] shrink-0" />}
            {!collapsed && <span>Tema</span>}
          </button>

          {!collapsed && (
            <div className="px-3 py-1">
              <p className="text-xs text-sidebar-foreground truncate">{user?.email || "—"}</p>
            </div>
          )}

          <button
            data-testid="logout-button"
            onClick={async () => { await signOut(); navigate("/login"); }}
            aria-label="Sair da conta"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-destructive hover:bg-destructive/10 transition-colors w-full"
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && "Sair"}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-foreground/40"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative flex flex-col w-64 bg-sidebar z-50 animate-slide-in" role="dialog" aria-modal="true" aria-label="Menu lateral">
            <div className="flex items-center justify-between h-14 px-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-sidebar-primary flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt={nomeEmpresa ?? "Logo"} className="h-full w-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  ) : (
                    <Building2 className="h-4 w-4 text-sidebar-primary-foreground" />
                  )}
                </div>
                <span className="text-sm font-bold text-sidebar-accent-foreground">{nomeEmpresa ?? BRAND}</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                aria-label="Fechar menu"
                className="text-sidebar-foreground/50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarNav
              config={nav}
              onItemClick={() => setSidebarOpen(false)}
            />
            <div className="px-2 pb-3 border-t border-border pt-3">
              <button
                onClick={async () => { await signOut(); navigate("/login"); }}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-destructive hover:bg-destructive/10 transition-colors w-full"
              >
                <LogOut className="h-[18px] w-[18px]" />
                Sair
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header
          className="border-b border-border flex items-center justify-between px-4 bg-background sticky top-0 z-30"
          style={{ minHeight: "3.5rem", paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <div className="flex items-center gap-3 lg:hidden">
            <Button data-tour="mobile-menu-btn" variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} aria-label="Abrir menu">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-sidebar-primary flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt={nomeEmpresa ?? "Logo"} className="h-full w-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                ) : (
                  <Building2 className="h-3.5 w-3.5 text-sidebar-primary-foreground" />
                )}
              </div>
              <span className="text-sm font-bold">{nomeEmpresa ?? BRAND}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {/* No mobile o seletor mora aqui, e não atrás do ☰: é o mesmo lugar
                dos painéis Admin e Superadmin, que não têm menu lateral. Dois
                seletores na mesma tela divergiriam em silêncio. */}
            <div className="lg:hidden">
              <ContextSwitcher compact />
            </div>
            <NotificationBell />
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {user?.email?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-6">
          <div className="animate-fade-in">
            <TitularPdfSync />
            <AvisoMfaSuperadmin />
            {children}
          </div>
        </main>

        {/* Mobile Bottom Nav — usa mobileItems do menu do painel ativo */}
        <nav
          aria-label="Navegação mobile"
          className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border flex justify-around py-2 z-30 bottom-nav-safe"
        >
          {mobileItems.map((item) => {
            const active = location.pathname === item.route;
            return (
              <Link
                key={item.id}
                to={item.route}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] transition-colors",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                <item.icon className="h-5 w-5" aria-hidden="true" />
                <span className="truncate max-w-[56px]">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>

    {/* TourRunningProvider — contexto compartilhado entre GuidedTour e TourFab */}
    <TourRunningProvider>
      {/* Tour guiado — montado no layout global para sobreviver à navegação entre rotas.
          O kill-switch vale para ELE também: era o único tour que o ignorava, e
          foi assim que o primeiro E2E completo de staging caiu — 37 specs com
          driver-popover interceptando cliques de usuários recém-criados.
          `?tour=1` (Refazer tour) continua passando por cima. */}
      {((TOURS_ENABLED && shouldShowTour) || forceTour) && (
        <Suspense fallback={null}>
          <GuidedTour forceStart={forceTour} />
        </Suspense>
      )}

      {/* Tours por tela. TOURS_ENABLED é constante congelada na carga do módulo:
          o componente sequer é montado quando os tours estão desligados, então o
          overlay não tem como aparecer no meio da sessão e interceptar cliques. */}
      {TOURS_ENABLED && (
        <Suspense fallback={null}>
          <ScreenTour forceStart={forceScreenTour} />
        </Suspense>
      )}

      {/* TourFab — botão flutuante ✦ TOUR / ✕ FECHAR disponível em todas as telas */}
      <Suspense fallback={null}>
        <TourFab />
      </Suspense>

      {/* Ponto de entrada único da ajuda: assistente, ajuda da tela, vídeos e
          tour, num só botão. Quatro afordâncias de ajuda competindo ensinam a
          pessoa a não usar nenhuma. */}
      <Suspense fallback={null}>
        <HelpAssistantProvider>
          <HelpLauncher />
        </HelpAssistantProvider>
      </Suspense>
    </TourRunningProvider>
    </TourMobileContext.Provider>
  );
}
